import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { getApiClient } from '@apps/web/lib/api_client';

// Throttle "still typing" pings so a fast typist sends at most one POST per
// window, and auto-clear a remote typer shortly after their last ping so a
// dropped client never leaves a stuck "is typing…" indicator.
const TYPING_RESEND_INTERVAL_MS = 3000;
const TYPING_STOP_DEBOUNCE_MS = 3000;
const TYPING_EXPIRY_MS = 5000;
const TYPING_SWEEP_INTERVAL_MS = 1000;

export type ChatTypingUser = {
    readonly userId: string;
    readonly displayName: string;
};

type TypingEntry = {
    readonly displayName: string;
    readonly expiresAt: number;
};

export function usePhoebeChatTyping({
    organizationId,
    runId,
    currentUserId,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly runId: string | null;
    readonly currentUserId: string | null;
    readonly enabled: boolean;
}): {
    readonly typingUsers: Array<ChatTypingUser>;
    readonly notifyTyping: () => void;
} {
    const [typingByUserId, setTypingByUserId] = useState<
        Record<string, TypingEntry>
    >({});
    const lastTrueSentAtMsRef = useRef(0);
    const stopTimerRef = useRef<number | null>(null);

    const sendTyping = useCallback(
        (typing: boolean) => {
            if (!enabled || !organizationId || !runId) {
                return;
            }
            void getApiClient()
                .post({
                    url: '/organizations/{organization_id}/runs/{run_id}/typing',
                    pathParams: {
                        organization_id: organizationId,
                        run_id: runId,
                    },
                    body: { typing },
                    bestEffort: true,
                })
                .catch(() => {
                    // Presence is best-effort; a dropped ping self-heals via the
                    // remote expiry sweep, so failures are intentionally ignored.
                });
        },
        [enabled, organizationId, runId],
    );

    const notifyTyping = useCallback(() => {
        if (!enabled || !organizationId || !runId) {
            return;
        }
        const nowMs = Date.now();
        if (nowMs - lastTrueSentAtMsRef.current > TYPING_RESEND_INTERVAL_MS) {
            lastTrueSentAtMsRef.current = nowMs;
            sendTyping(true);
        }
        if (stopTimerRef.current !== null) {
            window.clearTimeout(stopTimerRef.current);
        }
        stopTimerRef.current = window.setTimeout(() => {
            lastTrueSentAtMsRef.current = 0;
            sendTyping(false);
        }, TYPING_STOP_DEBOUNCE_MS);
    }, [enabled, organizationId, runId, sendTyping]);

    // Announce that we stopped typing when the chat unmounts or the run
    // changes, so teammates don't see a lingering indicator.
    useEffect(() => {
        return () => {
            if (stopTimerRef.current !== null) {
                window.clearTimeout(stopTimerRef.current);
                stopTimerRef.current = null;
            }
            if (lastTrueSentAtMsRef.current !== 0) {
                lastTrueSentAtMsRef.current = 0;
                sendTyping(false);
            }
        };
    }, [sendTyping]);

    useListenForOrganizationEvent(
        'chat_typing',
        (event) => {
            if (event.type !== 'chat_typing') {
                return;
            }
            const data = event.data;
            if (data.run_id !== runId || data.user_id === currentUserId) {
                return;
            }
            setTypingByUserId((previous) => {
                const next = { ...previous };
                if (data.typing) {
                    next[data.user_id] = {
                        displayName: data.display_name,
                        expiresAt: Date.now() + TYPING_EXPIRY_MS,
                    };
                } else {
                    delete next[data.user_id];
                }
                return next;
            });
        },
        { organizationId },
    );

    // Reset presence when the viewed run (or org) changes, so typers from a
    // previously-open chat don't linger in the new one until their TTL expires.
    useEffect(() => {
        setTypingByUserId({});
    }, [runId, organizationId]);

    // Drop expired typers so a missed "stopped" ping cannot wedge the indicator.
    // Gate the sweep on whether ANY typer is present (a boolean) rather than on
    // the map identity, so the interval starts when the first typer appears and
    // stops when the last clears — it ticks steadily instead of restarting on
    // every individual expiry.
    const hasTypers = Object.keys(typingByUserId).length > 0;
    useEffect(() => {
        if (!hasTypers) {
            return;
        }
        const intervalId = window.setInterval(() => {
            setTypingByUserId((previous) => {
                const nowMs = Date.now();
                const remaining = Object.fromEntries(
                    Object.entries(previous).filter(
                        ([, entry]) => entry.expiresAt > nowMs,
                    ),
                );
                return Object.keys(remaining).length ===
                    Object.keys(previous).length
                    ? previous
                    : remaining;
            });
        }, TYPING_SWEEP_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [hasTypers]);

    const typingUsers = useMemo(
        () =>
            Object.entries(typingByUserId).map(([userId, entry]) => ({
                userId,
                displayName: entry.displayName,
            })),
        [typingByUserId],
    );

    return { typingUsers, notifyTyping };
}
